"use client";

import { ReactNode, useState, useEffect, useMemo } from "react";
import { PrivyProvider } from "@privy-io/react-auth";
import { toSolanaWalletConnectors } from "@privy-io/react-auth/solana";
import { createSolanaRpc, createSolanaRpcSubscriptions } from "@solana/kit";
import { WalletSelectionProvider } from "@/contexts/WalletSelectionContext";
import { AuthSessionProvider } from "@/contexts/AuthSessionContext";
import { subscribePrivyReset } from "@/lib/privy-reset";

interface PrivyWalletProviderProps {
	children: ReactNode;
}

function requireEnv(value: string | undefined, name: string): string {
	if (!value) {
		throw new Error(`Missing required environment variable: ${name}`);
	}
	return value;
}

const clientId = process.env.NEXT_PUBLIC_PRIVY_APP_ID;
const productionRpcUrl =
	requireEnv(
		process.env.NEXT_PUBLIC_PRODUCTION_RPC ||
			process.env.NEXT_PUBLIC_SOLANA_RPC_URL,
		"NEXT_PUBLIC_PRODUCTION_RPC or NEXT_PUBLIC_SOLANA_RPC_URL"
	);
const developmentRpcUrl =
	requireEnv(
		process.env.NEXT_PUBLIC_DEVELOPMENT_RPC ||
			process.env.NEXT_PUBLIC_SOLANA_RPC_URL,
		"NEXT_PUBLIC_DEVELOPMENT_RPC or NEXT_PUBLIC_SOLANA_RPC_URL"
	);
const sharedRpcUrl = requireEnv(
	process.env.NEXT_PUBLIC_SOLANA_RPC_URL,
	"NEXT_PUBLIC_SOLANA_RPC_URL"
);

function toWsUrl(httpOrWsUrl: string): string {
	if (httpOrWsUrl.startsWith("wss://") || httpOrWsUrl.startsWith("ws://")) {
		return httpOrWsUrl;
	}
	if (httpOrWsUrl.startsWith("https://")) {
		return httpOrWsUrl.replace("https://", "wss://");
	}
	if (httpOrWsUrl.startsWith("http://")) {
		return httpOrWsUrl.replace("http://", "ws://");
	}
	return httpOrWsUrl;
}

if (!clientId) {
	throw new Error(
		"NEXT_PUBLIC_PRIVY_APP_ID is required in environment variables"
	);
}

/**
 * Hook to detect if Phantom wallet is available on desktop
 */
function usePhantomDetection() {
	const [hasPhantom, setHasPhantom] = useState(false);
	const [isMobile, setIsMobile] = useState(false);

	useEffect(() => {
		// Check if device is mobile
		const checkMobile = () => {
			return (
				/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
					navigator.userAgent
				) || window.innerWidth <= 768
			);
		};

		const mobile = checkMobile();
		setIsMobile(mobile);

		// Only check for Phantom on desktop
		if (!mobile) {
			const checkPhantom = () => {
				const solana = (window as any).solana;
				return !!(solana && solana.isPhantom);
			};

			// Initial check
			setHasPhantom(checkPhantom());

			// Retry mechanism for Phantom detection
			const maxRetries = 10;
			let retryCount = 0;
			const retryIntervals: ReturnType<typeof setTimeout>[] = [];

			const retryPhantomDetection = () => {
				if (retryCount >= maxRetries) {
					return;
				}

				const hasPhantomDetected = checkPhantom();
				if (hasPhantomDetected) {
					setHasPhantom(true);
					// Clear remaining retries
					retryIntervals.forEach(clearTimeout);
					return;
				}

				retryCount++;
				// Exponential backoff: 500ms, 1s, 2s, 4s, then 5s intervals
				const delay =
					retryCount <= 4
						? Math.min(500 * Math.pow(2, retryCount - 1), 4000)
						: 5000;

				const timeoutId = setTimeout(retryPhantomDetection, delay);
				retryIntervals.push(timeoutId);
			};

			// Start retry mechanism after initial check
			if (!checkPhantom()) {
				const initialRetryTimeout = setTimeout(retryPhantomDetection, 500);
				retryIntervals.push(initialRetryTimeout);
			}

			// Listen for Phantom events
			const handlePhantomReady = () => {
				setHasPhantom(true);
			};

			window.addEventListener("solana#initialized", handlePhantomReady);
			window.addEventListener("phantom#initialized", handlePhantomReady);

			// Cleanup function
			return () => {
				window.removeEventListener("solana#initialized", handlePhantomReady);
				window.removeEventListener("phantom#initialized", handlePhantomReady);
				retryIntervals.forEach(clearTimeout);
			};
		}
	}, []);

	return { hasPhantom, isMobile };
}

/**
 * Privy Provider with Solana configuration
 * Based on specifications in privy.md
 */
export function PrivyWalletProvider({ children }: PrivyWalletProviderProps) {
	const { hasPhantom, isMobile } = usePhantomDetection();

	// Bumping `resetKey` forces PrivyProvider to remount, clearing any login
	// flow state the SDK was holding from the previous session. See
	// `src/lib/privy-reset.ts` for the rationale.
	const [resetKey, setResetKey] = useState(0);
	useEffect(() => {
		return subscribePrivyReset(() => {
			setResetKey((k) => k + 1);
		});
	}, []);

	const loginMethods = useMemo((): Array<"email" | "sms" | "wallet" | "twitter"> => {
		if (isMobile) return ["email", "sms", "twitter"];
		if (hasPhantom) return ["email", "sms", "wallet", "twitter"];
		return ["email", "sms", "twitter"];
	}, [isMobile, hasPhantom]);

	const solanaConnectors = useMemo(
		() => toSolanaWalletConnectors({ shouldAutoConnect: false }),
		[]
	);

	const externalWalletsConfig = useMemo(
		() => ({
			solana: { connectors: solanaConnectors },
			ethereum: { connectors: [] as any[] },
			disableAllExternalWallets: false,
		}),
		[solanaConnectors]
	);

	const privyConfig = useMemo(
		() => ({
			loginMethods,
			solana: {
				rpcs: {
					"solana:mainnet": {
						rpc: createSolanaRpc(productionRpcUrl as any),
						rpcSubscriptions: createSolanaRpcSubscriptions(
							toWsUrl(productionRpcUrl as any)
						),
					},
					"solana:devnet": {
						rpc: createSolanaRpc(developmentRpcUrl as any),
						rpcSubscriptions: createSolanaRpcSubscriptions(
							toWsUrl(developmentRpcUrl as any)
						),
					},
					"solana:testnet": {
						rpc: createSolanaRpc(sharedRpcUrl as any),
						rpcSubscriptions: createSolanaRpcSubscriptions(
							toWsUrl(sharedRpcUrl as any)
						),
					},
				} as any,
			},
			appearance: {
				theme: "dark" as const,
				accentColor: "#768729" as `#${string}`,
				logo: "/icons/logo.gif",
				walletChainType: "solana-only" as const,
			},
			externalWallets: externalWalletsConfig,
			walletConnectCloudProjectId: undefined,
			embeddedWallets: {
				ethereum: {
					createOnLogin: "off" as const,
				},
				solana: {
					createOnLogin: "users-without-wallets" as const,
				},
				showWalletUIs: true,
				requireUserPasswordOnCreate: false,
				priceDisplay: {
					primary: "native-token" as const,
					secondary: null,
				},
			},
		}),
		[loginMethods, externalWalletsConfig]
	);

	return (
		<PrivyProvider key={resetKey} appId={clientId!} config={privyConfig}>
			<WalletSelectionProvider>
				<AuthSessionProvider>{children}</AuthSessionProvider>
			</WalletSelectionProvider>
		</PrivyProvider>
	);
}
